import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { teacherModuleAPI } from '../services/api';

const STORAGE_KEY = 'supervisorViewTeacherId';

function teacherLabel(t) {
  if (t.label) return t.label;
  const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
  const base = name || t.email || 'Teacher';
  if (t.supervisorName && t.reportingGroup === 'other') {
    return `${base} (Supervisor: ${t.supervisorName})`;
  }
  return base;
}

/**
 * Teacher picker for teaching leads and sales supervisors on Teacher Home / calendar views.
 * @param {{ includeAdmin?: boolean }} [options] — when true, also loads teachers for admin (e.g. Reports filters).
 */
export function useSupervisorTeacherPicker(options = {}) {
  const { includeAdmin = false } = options;
  const { user, isTeachingLead, isCountryManager, isAdmin, isBatchOps } = useAuth();
  const enabled = isTeachingLead() || isCountryManager() || isBatchOps() || (includeAdmin && isAdmin());
  const ownTeacherId = user?.teacherId ? String(user.teacherId) : null;

  const [selectedTeacherId, setSelectedTeacherId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return sessionStorage.getItem(STORAGE_KEY) || '';
  });

  const teachersQuery = useQuery(
    ['teachersForSupervisorPicker'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { enabled, staleTime: 5 * 60_000 }
  );

  const teachers = useMemo(() => {
    const raw = teachersQuery.data || [];
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((t) => {
        const id = String(t.id);
        const base = teacherLabel(t);
        return {
          id,
          label: ownTeacherId && id === ownTeacherId ? `${base} (you)` : base,
          first_name: t.first_name,
          last_name: t.last_name,
          email: t.email,
          timezone: t.timezone || null,
          isSelf: ownTeacherId && id === ownTeacherId,
          supervisorName: t.supervisorName || null,
          reportingGroup: t.reportingGroup || null,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [teachersQuery.data, ownTeacherId]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTeacherId) {
      sessionStorage.setItem(STORAGE_KEY, selectedTeacherId);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [enabled, selectedTeacherId]);

  useEffect(() => {
    if (!enabled || teachers.length === 0) return;
    const stillValid = teachers.some((t) => t.id === selectedTeacherId);
    if (!selectedTeacherId || !stillValid) {
      const prefer =
        ownTeacherId && teachers.some((t) => t.id === ownTeacherId)
          ? ownTeacherId
          : teachers[0].id;
      setSelectedTeacherId(prefer);
    }
  }, [enabled, teachers, selectedTeacherId, ownTeacherId]);

  const selectedTeacher = useMemo(
    () => teachers.find((t) => t.id === selectedTeacherId) || null,
    [teachers, selectedTeacherId]
  );

  return {
    isSupervisorPicker: enabled,
    teachers,
    selectedTeacherId: enabled ? selectedTeacherId : null,
    selectedTeacherName: selectedTeacher?.label || '',
    selectedTeacherTimezone: selectedTeacher?.timezone || null,
    setSelectedTeacherId,
    teachersLoading: teachersQuery.isLoading,
    teachersError: teachersQuery.isError,
    ready: enabled
      ? teachersQuery.isLoading
        ? false
        : teachers.length === 0 || !!selectedTeacherId
      : true,
    ownTeacherId,
    viewingOwnSchedule: !!(ownTeacherId && selectedTeacherId === ownTeacherId),
  };
}
